import { Breadcrumbs } from "@/components/breadcrumbs/Breadcrumbs.tsx";
import {
  ConfirmationModal,
  Page,
  useModals,
  Button,
  ButtonIcon,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Skeleton,
  Stack,
} from "@skailer-com/uikit";
import { useTitle } from "@/hooks/use-title.hook.ts";
import NotFound from "@/pages/NotFound.tsx";
import { GoalCheckInEditModal } from "@/pages/talent/goals/widgets/GoalCheckInEditModal.tsx";
import { GoalConfirmationDeleteWithSubGoalsModal } from "@/pages/talent/goals/widgets/GoalConfirmationDeleteWithSubGoalsModal.tsx";
import { GoalEditModal } from "@/pages/talent/goals/widgets/GoalEditModal.tsx";
import { GoalDetailsContent } from "@/pages/talent/goals/details/components/GoalDetailsContent.tsx";
import { useGoal, useGoalDelete } from "@/queries/goals.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiGoal, ApiGoalOwnerType } from "@/types/goals.types.ts";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

function flattenGoalChain(goal: ApiGoal | null): ApiGoal[] {
  const chain: ApiGoal[] = [];
  let current = goal;
  while (current) {
    chain.unshift(current);
    current = current.parent_goal as ApiGoal | null;
  }
  return chain;
}

export function GoalDetails() {
  const { t } = useTranslation();
  const modalsService = useModals();
  const { id } = useParams();
  const { openModal } = useModals();
  const navigate = useNavigate();

  const goalId = id ? parseInt(id) : undefined;
  const { data: goal, isLoading } = useGoal(goalId ?? 0, !!goalId);
  const goalDeleteMutation = useGoalDelete();

  const handleEdit = () => {
    if (goalId) {
      openModal(GoalEditModal, { goalId });
    }
  };

  const handleDelete = () => {
    if (!goal) return;

    const onDeleted = () => {
      if ((window.history?.length && window.history.length > 1) || window.history.state?.idx) {
        navigate(-1);
      } else {
        navigate("/", { replace: true });
      }

      analyticsService.trackEvent(analyticEvents.goals.goalDeleted, {
        [analyticProperties.id]: goal.id,
      });
    };

    if (goal.has_children) {
      modalsService.openModal(GoalConfirmationDeleteWithSubGoalsModal, {
        goalId: goal.id,
        onDeleted,
      });
    } else {
      modalsService.openModal(ConfirmationModal, {
        question: t("goals:delete_goal"),
        text: t("goals:delete_goal_confirmation"),
        acceptTitle: t("common:delete"),
        onAccept: async () => {
          await goalDeleteMutation.mutateAsync({ id: goal.id });
          onDeleted();
        },
      });
    }
  };

  const handleAddCheckIn = () => {
    if (!goal) return;
    openModal(GoalCheckInEditModal, { goalId: goal.id });
  };

  useTitle(goal?.title, t("goals:goals"));

  if (isLoading) {
    return (
      <Page>
        <Page.Header title={`${t("common:loading")}...`} />
        <Page.Content>
          <Stack gap={"lg"} className={"container-sm m-auto"}>
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-48 w-full" />
          </Stack>
        </Page.Content>
      </Page>
    );
  }

  if (!goal) {
    return <NotFound />;
  }

  const chain = goal.parent_goal_chain ? flattenGoalChain(goal.parent_goal_chain) : [];
  const topGoal = chain[0] ?? goal;
  const isCompanyGoal = topGoal.owner_type === ApiGoalOwnerType.company;
  const rootLink = isCompanyGoal ? "/goals/strategic" : "/goals/initiatives";
  const breadcrumbItems = [
    {
      id: "root",
      title: isCompanyGoal ? t("goals:strategic_goals") : t("goals:initiatives_goals"),
      to: rootLink,
    },
    ...chain.map((parentGoal, index) => ({
      id: parentGoal.id,
      title: parentGoal.title,
      to:
        index === 0 && isCompanyGoal
          ? `/goals/strategic/${parentGoal.id}`
          : `/goals/${parentGoal.id}`,
    })),
  ];

  return (
    <Page>
      <Page.Header backSlot={<Breadcrumbs items={breadcrumbItems} />}>
        <Stack direction={"horizontal"} gap={"sm"}>
          {goal?.acl.can_edit && (
            <Button variant={"secondary"} onClick={handleEdit}>
              {t("common:edit")}
            </Button>
          )}
          {goal?.acl.can_edit ? (
            <Button onClick={handleAddCheckIn}>{t("common:update_progress")}</Button>
          ) : (
            <></>
          )}
          {goal?.acl.can_delete && (
            <DropdownMenu
              trigger={
                <ButtonIcon
                  icon={<FontAwesomeIcon icon="fa-light fa-ellipsis" />}
                  variant={"secondary"}
                />
              }
            >
              <DropdownMenuItem
                variant={"menuDanger"}
                onClick={handleDelete}
                leftIcon={<FontAwesomeIcon icon="fa-light fa-trash" />}
                title={t("common:delete") + "..."}
              />
            </DropdownMenu>
          )}
        </Stack>
      </Page.Header>
      <Page.Content>
        <GoalDetailsContent goalId={goalId!} />
      </Page.Content>
    </Page>
  );
}
